import { Injectable } from '@angular/core';

export interface NotificationTrigger {
	id: string;
	module: string;
	label: string;
	desc: string;
	channelsAllowed?: string[];
}

export interface NotificationTagGroup {
	key: string;
	label: string;
	tags: string[];
}

export interface NotificationActionConfig {
	enabled: boolean;
	methods: string[];
	users: string[];
	subject?: string;
	blocks?: NotificationBlock[];
	masterUpdatedAt?: number;
}

export interface NotificationTemplate {
	id: string;
	channel: 'email';
	module: string;
	name: string;
	updated: string;
	usedBy: number;
	subject: string;
	body: string;
	blocks?: NotificationBlock[];
	updatedAt?: number;
}

export interface NotificationBlock {
	id: string;
	type: string;
	text?: string;
	html?: string;
	brand?: string;
	accent?: string;
	label?: string;
	url?: string;
	color?: string;
	src?: string;
	alt?: string;
	height?: number;
	logoUrl?: string;
	logoWidth?: number;
	rows?: { k: string; v: string }[];
	columns?: number;
	headerRow?: boolean;
	headers?: string[];
	cells?: string[][];
	dynamic?: boolean;
	source?: string;
	copyright?: string;
	companyInfo?: string;
	address?: string;
	contactEmail?: string;
	socials?: { network: 'linkedin' | 'facebook' | 'instagram'; url: string }[];
	children?: NotificationBlock[];
	width?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// UI constants
// ─────────────────────────────────────────────────────────────────────────────

export const NOTIFICATION_MODULES = [
	{ key: 'quoting',     label: 'Quoting' },
	// After Quoting rather than before it, though a Deal comes first in the lifecycle: the tab
	// strip's first entry is the default tab, so leading with the CRM would move every
	// CRM-enabled organisation off Quoting without anybody asking for that.
	{ key: 'crm',         label: 'CRM' },
	{ key: 'projects',    label: 'Projects' },
	{ key: 'workhub',     label: 'WorkHub' },
	{ key: 'snagg',       label: 'Snagg' },
	{ key: 'procurement', label: 'Procurement' },
	{ key: 'inventory',   label: 'Inventory' },
	{ key: 'catalogue',   label: 'Catalogue' },
	{ key: 'accounting',  label: 'Accounting' },
	{ key: 'auth',        label: 'Auth' },
	{ key: 'portal',      label: 'Portal' },
];

export const NOTIFICATION_CHANNELS = [
	{ key: 'email', label: 'Email' },
	{ key: 'push', label: 'Push notification' },
	{ key: 'alert', label: 'Platform alert' },
];

export const NOTIFICATION_PROJECT_ROLES = [
	{ key: 'role.manager',       label: 'Project Manager',    kind: 'role', hint: 'Manages the project end-to-end' },
	{ key: 'role.customer',      label: 'Customer',           kind: 'role', hint: 'Emails the project\'s customer when a trigger targets the customer role' },
	{ key: 'role.engineer',      label: 'Engineer',           kind: 'role', hint: 'Assigned to deliver the work; resolves to project-pinned engineers intersected with the work-order / task assignees' },
	{ key: 'role.finance',       label: 'Finance & Accounts', kind: 'role', hint: 'Reconciles invoices, payments and accounting integrations; resolves to project-pinned finance users with the Manage Billing permission' },
];

export interface ModuleHelpPoint {
	icon: string;
	label: string;
	text: string;
}
export interface ModuleDiagramNode {
	icon: string;
	label: string;
}
export interface ModuleDiagram {
	trigger: string;
	audience: ModuleDiagramNode[];
}
export interface ModuleHelp {
	intro: string;
	diagram?: ModuleDiagram;
	points?: ModuleHelpPoint[];
}


export interface TriggerHelp {
	fires: string;
	note?: string;
	tip?: string;
}

export const MODULE_HELP: { [moduleKey: string]: ModuleHelp } = {
	quoting: {
		intro: 'Quoting notifications keep whoever owns a quote informed as it moves through review and as your customer interacts with the online proposal.',
		diagram: {
			trigger: 'Quote / customer event',
			audience: [
				{ icon: 'wq wq-lock-closed', label: 'Quote assignee (always)' },
				{ icon: 'wq wq-user', label: 'People you pick' },
			],
		},
		points: [
			{ icon: 'wq wq-user', label: 'Who receives it', text: 'For most of these, the quote\'s assignee is always notified automatically — you don\'t need to configure anything. Anyone you add as a recipient is notified as well.' },
		],
	},
	projects: {
		intro: 'A single notification that fires as project invoices are pushed into your accounting system.',
		diagram: {
			trigger: 'Invoice sent to accounts',
			audience: [
				{ icon: 'wq wq-filter', label: 'Finance users you pick (Manage Billing)' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Who receives it', text: 'The people you select — typically your Finance & Accounts role, which resolves to the finance users assigned to that project.' },
			{ icon: 'wq wq-lock-closed', label: 'Permission', text: 'Only users with the Manage Billing permission can be chosen as recipients.' },
		],
	},
	workhub: {
		intro: 'WorkHub notifications keep the people delivering the work informed. Nobody is notified unless you select them below — you pick who to notify for each one. Recipients are resolved per project using roles:',
		diagram: {
			trigger: 'Job / task event',
			audience: [
				{ icon: 'wq wq-team', label: 'Project Manager (if picked)' },
				{ icon: 'wq wq-team', label: 'Engineer = on the job + has Engineer role' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Project Manager', text: 'The user(s) you\'ve given the Project Manager role on the project.' },
			{ icon: 'wq wq-team', label: 'Engineer', text: 'Resolves to the engineers actually on that specific job. A person is notified only if they BOTH hold the Engineer role on the project AND are assigned to that job / booking. If no one matches, nobody is notified.' },
			{ icon: 'wq wq-info', label: 'Setting it up', text: 'Choose who fills each role per project, or set them in bulk with "Apply role defaults to projects". You can also add specific users directly.' },
		],
	},
	snagg: {
		intro: 'Snagg notifications tell you when someone adds a Snagg (a comment, image, expense or material list) to a project, from the Snagg app or the web. You pick who to notify below.',
		diagram: {
			trigger: 'Snagg added',
			audience: [
				{ icon: 'wq wq-team', label: 'Project Manager (if picked)' },
				{ icon: 'wq wq-user', label: 'People you pick' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Project Manager', text: 'For a new Snagg, the user(s) you\'ve given the Project Manager role on that project.' },
			{ icon: 'wq wq-user', label: 'Who receives it', text: 'The specific users you select. Whoever added the Snagg isn\'t notified about their own.' },
		],
	},
	procurement: {
		intro: 'Procurement notifications track stock requests and purchase orders. You pick who to notify for each one below.',
		diagram: {
			trigger: 'Request / PO event',
			audience: [
				{ icon: 'wq wq-filter', label: 'Users you pick (Procurement permission)' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Who receives it', text: 'The specific users you select.' },
			{ icon: 'wq wq-lock-closed', label: 'Permission', text: 'Only users with the Procurement permission can be chosen as recipients.' },
		],
	},
	inventory: {
		intro: 'Inventory notifications fire on stock movements and adjustments in your warehouses — goods received or dispatched, stock made free, reduced (moved to a bin), assigned to another job, or manually adjusted. You pick who to notify for each one below.',
		diagram: {
			trigger: 'Stock movement / adjustment',
			audience: [
				{ icon: 'wq wq-filter', label: 'Users you pick (Warehouse permission)' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Who receives it', text: 'The specific users you select.' },
			{ icon: 'wq wq-lock-closed', label: 'Permission', text: 'Only users with the Warehouse permission can be chosen as recipients.' },
		],
	},
	catalogue: {
		intro: 'Catalogue notifications keep catalogue owners and dealers informed as access to shared catalogues is requested and granted.',
		diagram: {
			trigger: 'Catalogue access event',
			audience: [
				{ icon: 'wq wq-user', label: 'Owner: people you pick + catalogue emails' },
				{ icon: 'wq wq-user', label: 'Dealer: the requesting user' },
			],
		},
		points: [
			{ icon: 'wq wq-team', label: 'Who receives it', text: 'For requests you receive as the owner: the recipients you select, plus the notification emails set on the catalogue. Approval and decline notices go to the dealer user who made the request.' },
		],
	},
	accounting: {
		intro: 'Billing notifications about your WeQuote subscription and invoices.',
		diagram: {
			trigger: 'Billing / invoice event',
			audience: [
				{ icon: 'wq wq-lock-closed', label: 'Billing contact (always)' },
				{ icon: 'wq wq-user', label: 'People you pick' },
			],
		},
		points: [
			{ icon: 'wq wq-lock-closed', label: 'Who receives it', text: 'Your organisation\'s billing contact is always emailed. For invoices you can add other recipients as well.' },
		],
	},
	auth: {
		intro: 'Account and security emails. These are essential, so they are always sent and can\'t be turned off.',
	},
	portal: {
		intro: 'Customer-portal emails sent to the people who use your project portal — invitations, password resets, and notices that a quote, change order or invoice is ready to view. These are essential, so they are always sent and can\'t be turned off.',
		diagram: {
			trigger: 'Portal invite / reset / document',
			audience: [
				{ icon: 'wq wq-user', label: 'The portal user (always)' },
			],
		},
		points: [
			{ icon: 'wq wq-user', label: 'Who receives it', text: 'The portal user in question — the invitation or password-reset link is emailed straight to them.' },
		],
	},
};

export const TRIGGER_HELP: { [triggerId: string]: TriggerHelp } = {
	// ── Quoting ──────────────────────────────────────────────────────────────
	'quote.submit.review':       { fires: 'Fires when a quote is submitted for review or approval.' },
	'quote.review.result':       { fires: 'Fires when an approver approves or rejects a submitted quote.' },
	'quote.passed.manually':     { fires: 'Fires when a quote is manually marked as passed, skipping review.' },
	'quote.proposal.viewed':     { fires: 'Fires each time your customer opens the online proposal.' },
	'quote.changeorder.viewed':  { fires: 'Fires each time your customer opens a change-order link.' },
	'quote.proposal.comment':    { fires: 'Fires when your customer comments on the online proposal.' },
	'quote.changeorder.comment': { fires: 'Fires when your customer comments on a change order.' },
	'quote.proposal.accepted':   { fires: 'Fires when your customer accepts the proposal online.' },
	'quote.changeorder.accepted':{ fires: 'Fires when your customer accepts a change order online.' },
	'quote.acceptance.confirmation': { fires: 'A confirmation email sent automatically when a quote is accepted.', note: 'Recipients can\'t be changed — sent to the quote\'s assignee and your organisation\'s customer-accept email.' },
	'quote.quota.exceeded':      { fires: 'Fires when your organisation uses up its monthly quoting quota.', note: 'Emailed to your organisation\'s billing contact.' },
	'quote.expired':             { fires: 'Fires overnight when a sent quote passes its expiry date.' },
	'quote.salesorder.completed': { fires: 'Fires when a sales order is marked as completed — the "time to invoice" signal for whoever raises your invoices.', note: 'Sent straight away. The daily "Uninvoiced sales order or quote" summary still runs separately.' },

	// ── Projects ─────────────────────────────────────────────────────────────
	'project.invoice.sent.accounts': { fires: 'Fires when a project invoice is pushed to your accounting system (Xero, QuickBooks or Zoho).' },
	'project.invoice.paid':          { fires: 'Fires when an invoice or deposit is paid in full, and when a partial payment is received that still leaves a balance outstanding.', note: 'Manual and Stripe payments notify straight away. Payments that come in through your accounting system (Xero, QuickBooks or Zoho) only notify once the invoice is fully paid — not on each partial payment.' },
	'project.invoice.overdue':       { fires: 'Fires overnight when an outstanding invoice or deposit passes its due date without being paid in full.' },
	'project.invoice.payment.pending': { fires: 'Fires when a customer pays online with a delayed method (bank debit / ACH / BACS) and the payment is still clearing.', note: 'The invoice isn\'t paid yet — "Invoice paid / partially paid" fires separately once the money clears.' },
	'project.invoice.payment.failed':  { fires: 'Fires when a clearing payment fails and the money never arrives.' },

	// ── WorkHub ──────────────────────────────────────────────────────────────
	'workhub.job.assigned':      { fires: 'Fires when an engineer is assigned to a job.' },
	'workhub.booking.changed':   { fires: 'Fires when a job booking is rescheduled or reassigned.', note: 'Note-only edits don\'t fire this — they use the booking note triggers instead.' },
	'workhub.booking.note.added':  { fires: 'Fires when a note is first added to a booking.' },
	'workhub.booking.note.edited': { fires: 'Fires when an existing booking note is changed.', note: 'Clearing a note doesn\'t fire anything. Unlike "Booking changed", these aren\'t limited to the next 3 days — any booking that hasn\'t finished yet.' },
	'workhub.task.note.added':   { fires: 'Fires when a note is added to a task.' },
	'workhub.task.completed':    { fires: 'Fires when any task is marked complete, billable or not.', note: 'For the "time to invoice" signal on billable tasks specifically, use "Billable task complete" — a billable task fires both.' },
	'workhub.stock.variation':   { fires: 'Fires when the stock used on a task differs from what was planned.' },
	'workhub.report.created':    { fires: 'Fires when a report is created on a job or task.' },
	'workhub.follow_up.requested':{ fires: 'Fires when a follow-up visit is requested on a task.' },
	'workhub.follow_up.resolved':{ fires: 'Fires when a requested follow-up is reopened or completed.' },
	'workhub.snagg.session.overtime': { fires: 'Fires when a clocked-in user\'s work session runs over.', note: 'Push only, sent to the clocked-in user — not configurable.' },
	'workhub.snagg.break.overtime':   { fires: 'Fires when a clocked-in user\'s break runs over.', note: 'Push only, sent to the clocked-in user — not configurable.' },
	'workhub.job.completed':     { fires: 'Fires when a job (work order) is marked complete.' },
	'workhub.task.billable.complete': { fires: 'Fires only when a billable task is completed — the "time to invoice" signal for Finance.', note: 'Separate from "Task completed" (which covers all tasks). A billable task fires both, so send this one to Finance and keep general completions on "Task completed".' },
	'workhub.timesheet.approval': { fires: 'A nightly summary, per engineer, of added or amended time entries awaiting approval.', note: 'Sent overnight — one email per engineer with entries to approve.' },

	// ── Procurement ──────────────────────────────────────────────────────────
	'proc.stock.request.submitted': { fires: 'Fires when a stock or purchase request is submitted.' },
	'proc.request.finalised':    { fires: 'Fires when a purchase request is finalised.', note: 'The person who submitted the request is always notified — add anyone else here.' },
	'proc.request.stock.items':  { fires: 'Fires when a request is approved and has items to be fulfilled from stock on hand.' },
	'proc.request.overdue':      { fires: 'Fires overnight when an approved request passes its due date without being fulfilled.' },
	'proc.po.sent.supplier':     { fires: 'Sends the purchase order to the supplier.', note: 'Sent to the supplier email entered when sending the PO, with the sender CC\'d — not configurable.' },
	'proc.po.sent.internal':     { fires: 'Fires when a PO is sent, as an internal copy.' },
	'proc.po.confirmed':         { fires: 'Fires when a supplier confirms a purchase order.' },
	'proc.po.not.received':      { fires: 'Fires overnight when a sent purchase order passes its expected delivery date without being received.' },
	'proc.po.sent.integration':  { fires: 'Fires when a purchase order is pushed to a connected integration (Xero or QuickBooks).' },

	// ── Inventory ────────────────────────────────────────────────────────────
	'inv.goods.in.complete':     { fires: 'Fires when a goods-in (receiving) transaction is completed.' },
	'inv.goods.out.complete':    { fires: 'Fires when a goods-out (dispatch) transaction is completed.' },
	'inv.goods.in.not.received': { fires: 'Fires when a dispatched stock transfer is not marked as received at its destination within 3 days.' },
	'inv.goods.out.picking.ready': { fires: 'Fires when a transfer\'s items are ready to be picked.' },
	'inv.goods.out.dispatch.ready':{ fires: 'Fires when a transfer\'s items are picked and ready to be dispatched.' },
	'inv.stock.free':            { fires: 'Allocated stock returned to free stock (scanner or portal). A portal destination also creates a Goods Out transfer (not auto-dispatched).' },
	'inv.stock.reassigned':      { fires: 'Scanner only. Stock made free and moved to another project site in one action.' },
	'inv.stock.adjusted':        { fires: 'Portal only. A user changed an on-hand quantity at a location (up or down).' },

	// ── Catalogue ────────────────────────────────────────────────────────────
	'cat.access.request':        { fires: 'Fires when a dealer requests access to your catalogue.' },
	'cat.access.linked':         { fires: 'Fires when a dealer is automatically linked to your catalogue.' },
	'cat.access.approved':       { fires: 'Fires when your request to access another catalogue is approved.', note: 'Goes to the user who made the request.' },
	'cat.access.cancelled':      { fires: 'Fires when catalogue access is declined or later removed.', note: 'Goes to the user who made the request.' },
	'cat.subscription.pending.digest': { fires: 'A nightly summary of catalogue requests still awaiting your response.' },
	'quote.digest.uninvoiced':         { fires: 'A daily summary of completed quotes / sales orders that have not been invoiced yet.' },

	// ── Accounting ───────────────────────────────────────────────────────────
	'acc.package.changed':       { fires: 'Fires when your organisation changes its WeQuote plan.', note: 'Emailed to your billing contact.' },
	'acc.invoice.issued':        { fires: 'Your monthly WeQuote invoice.', note: 'Always emailed to your billing contact — add anyone else here.' },
	'acc.account.cancellation.requested.internal': { fires: 'Fires when a customer requests cancellation from the upgrade account screen.', note: 'Nothing changes on their account — a member of staff needs to action it.' },
	'acc.payment.failed':        { fires: 'Fires when a payment to WeQuote fails.', note: 'Always emailed to your billing contact — add anyone else here.' },

	// ── Snagg ────────────────────────────────────────────────────────────────
	'snagg.user.connected':      { fires: 'Fires when a new user starts using the Snagg app in your organisation.' },
	'snagg.created':             { fires: 'Fires when anyone adds a Snagg (comment, image, expense or material list) from the Snagg app or the web.' },

	// ── Portal ───────────────────────────────────────────────────────────────
	'portal.user.invite':            { fires: 'Fires when you invite someone to access a project in the customer portal.', note: 'Always emailed to the invited person with their invitation link — not configurable.' },
	'portal.quote.added':            { fires: 'Fires when you publish a quote to the customer portal and send it to the project\'s portal users.', note: 'Always emailed to the project portal users you pick — not configurable.' },
	'portal.change_order.added':     { fires: 'Fires when you publish a change order to the customer portal and send it to the project\'s portal users.', note: 'Always emailed to the project portal users you pick — not configurable.' },
	'portal.invoice.added':          { fires: 'Fires when you send an issued invoice to the project\'s portal users from the Send Invoice window.', note: 'Always emailed to the project portal users you pick — not configurable.' },
	'portal.password.reset.request': { fires: 'Fires when a portal user requests a password-reset link from the portal sign-in page.', note: 'Always emailed to the portal user with their reset link — not configurable.' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Tag groups — drive the template editor's tag picker
// ─────────────────────────────────────────────────────────────────────────────

export const COMMON_TAGS: NotificationTagGroup = {
	key: 'common',
	label: 'Common',
	tags: ['{Org Name}', '{Sender Name}', '{Recipient Name}', '{Action URL}'],
};

export const ENTITY_TAG_GROUPS: { [entity: string]: NotificationTagGroup } = {
	quote:           { key: 'quote',           label: 'Quote',           tags: ['{Quote Number}', '{Quote Title}', '{Quote Net Total}', '{Quote Total}', '{Customer Name}', '{Project Name}', '{Review Result}', '{Review Notes}', '{Comment}'] },
	changeOrder:     { key: 'changeOrder',     label: 'Change order',    tags: ['{Quote Number}', '{Quote Net Total}', '{Quote Total}', '{Project Name}', '{Customer Name}'] },
	salesOrder:      { key: 'salesOrder',      label: 'Sales order',     tags: ['{Quote Number}', '{Quote Title}', '{Quote Net Total}', '{Quote Total}', '{Customer Name}', '{Project Name}'] },
	po:              { key: 'po',              label: 'Purchase order',  tags: ['{PO Number}', '{Supplier Name}', '{Project Name}', '{Customer Name}', '{Integration Name}'] },
	invoice:         { key: 'invoice',         label: 'Invoice',         tags: ['{Invoice Number}', '{Customer Name}', '{Project Name}', '{Integration Name}', '{Amount}', '{Invoice Type}', '{Payment Status}', '{Payment Amount}', '{Amount Paid}', '{Amount Remaining}', '{Due Date}'] },
	request:         { key: 'request',         label: 'Request',         tags: ['{Request Number}', '{Request Name}', '{Project Name}'] },
	task:            { key: 'task',            label: 'Task',            tags: ['{Task Title}', '{Project Name}', '{Due Date}', '{Note}', '{Added By}'] },
	job:             { key: 'job',             label: 'Job',             tags: ['{Job Name}', '{Job Number}', '{Job Type}', '{Job Address}', '{Project Name}', '{Customer Name}', '{Booked By}', '{Scheduled Date}'] },
	goodsIn:         { key: 'goodsIn',         label: 'Goods in',        tags: ['{Warehouse Name}', '{Origin}', '{Origin Address}', '{Destination}', '{Destination Address}', '{Transaction Number}', '{PO Number}', '{Project Name}'] },
	goodsOut:        { key: 'goodsOut',        label: 'Goods out',       tags: ['{Warehouse Name}', '{Origin}', '{Origin Address}', '{Destination}', '{Destination Address}', '{Transaction Number}', '{Project Name}'] },
	stockAction:     { key: 'stockAction',     label: 'Stock action',    tags: ['{Item}', '{Quantity}', '{Warehouse Name}', '{Project Name}', '{Destination}', '{Location}', '{Serial Number}', '{Transaction Number}', '{Status}', '{Transfer Note}'] },
	stockAdjust:     { key: 'stockAdjust',     label: 'Stock adjustment', tags: ['{Item}', '{Warehouse Name}', '{Location}', '{Old Quantity}', '{New Quantity}', '{Change}'] },
	catalogue:       { key: 'catalogue',       label: 'Catalogue',       tags: ['{Catalogue Name}'] },
	catalogueDigest: { key: 'catalogueDigest', label: 'Catalogue digest', tags: ['{Pending Count}', '{Digest Subject}', '{Digest Heading}', '{Digest Intro}', '{Requests List}'] },
	timesheet:       { key: 'timesheet',       label: 'Timesheet',       tags: ['{Engineer Name}', '{Project Name}', '{Customer Name}', '{Entry Count}'] },
	uninvoiced:      { key: 'uninvoiced',      label: 'Uninvoiced digest', tags: ['{Quote Count}'] },
	organisation:    { key: 'organisation',    label: 'Organisation',    tags: ['{Package Name}', '{Old Package}', '{New Package}', '{Spent Amount}', '{Quota Amount}', '{Billing Period}', '{Amount}', '{Reason}'] },
	snaggUser:       { key: 'snaggUser',       label: 'Snagg user',      tags: ['{User Name}'] },
	snagg:           { key: 'snagg',           label: 'Snagg',           tags: ['{Snagg Type}', '{Snagg Title}', '{Snagg Details}', '{Job Name}', '{Project Name}', '{Customer Name}'] },
	crmRecord:       { key: 'crmRecord',       label: 'Lead or Deal',    tags: ['{Record Type}', '{Record Title}', '{Record Value}', '{Stage Name}', '{Customer Name}', '{Owner Name}', '{Assigned To}'] },
	crmActivity:     { key: 'crmActivity',     label: 'CRM activity',    tags: ['{Activity Title}', '{Activity Due}', '{Assignee Name}'] },
	crmStage:        { key: 'crmStage',        label: 'Stage change',    tags: ['{Stage From}', '{Stage To}'] },
	crmDigest:       { key: 'crmDigest',       label: 'CRM summary',     tags: ['{Item Count}', '{Summary}'] },
	stockVariation:  { key: 'stockVariation',  label: 'Stock variation', tags: ['{Task Title}', '{Job Name}', '{Project Name}', '{Stock Variation}'] },
	bookingChange:   { key: 'bookingChange',   label: 'Booking change',  tags: ['{Changes}', '{Job Name}', '{Job Number}', '{Job Type}', '{Job Address}', '{Project Name}', '{Customer Name}', '{Booked By}', '{Scheduled Date}'] },
	bookingNote:     { key: 'bookingNote',     label: 'Booking note',    tags: ['{Note}', '{Added By}', '{Job Name}', '{Job Number}', '{Project Name}', '{Customer Name}', '{Scheduled Date}'] },
	report:          { key: 'report',          label: 'Report',          tags: ['{Report Name}', '{Task Title}', '{Job Name}', '{Project Name}', '{Customer Name}', '{Attachment Note}'] },
	followUp:        { key: 'followUp',        label: 'Follow-up',       tags: ['{Task Title}', '{Job Name}', '{Project Name}', '{Follow-up Note}', '{Resolution}'] },
	user:            { key: 'user',            label: 'User',            tags: ['{Verification Code}', '{OTP Code}', '{Reset Link}', '{Invite Link}', '{Inviter Name}', '{Contact Name}', '{Contact Email}', '{Mobile}'] },
	portalInvite:    { key: 'portalInvite',    label: 'Portal invite',   tags: ['{Inviter Name}', '{Project Name}', '{Project Number}', '{Message}'] },
	portalQuote:       { key: 'portalQuote',       label: 'Portal quote',        tags: ['{Sender Name}', '{Project Name}', '{Project Number}', '{Quote Number}', '{Revision Number}', '{Message}'] },
	portalChangeOrder: { key: 'portalChangeOrder', label: 'Portal change order', tags: ['{Sender Name}', '{Project Name}', '{Project Number}', '{Quote Number}', '{Revision Number}', '{Message}'] },
	portalInvoice:     { key: 'portalInvoice',     label: 'Portal invoice',      tags: ['{Sender Name}', '{Project Name}', '{Project Number}', '{Invoice Number}', '{Due Date}', '{Amount}', '{Invoice URL}', '{Portal URL}', '{Invoice Button Label}', '{Message}'] }
};

export const NOTIFICATION_TAGS: string[] = (() => {
	const all = new Set<string>(COMMON_TAGS.tags);
	Object.keys(ENTITY_TAG_GROUPS).forEach(k => ENTITY_TAG_GROUPS[k].tags.forEach(t => all.add(t)));
	return Array.from(all);
})();


// Used by the master template editor when scoped only by module (no specific trigger).
export const MODULE_ENTITIES: { [moduleKey: string]: string[] } = {
	quoting:     ['quote', 'changeOrder', 'salesOrder', 'organisation', 'uninvoiced'],
	projects:    ['invoice'],
	workhub:     ['job', 'task', 'stockVariation', 'report', 'bookingChange', 'bookingNote', 'followUp', 'timesheet'],
	snagg:       ['snaggUser', 'snagg'],
	crm:         ['crmRecord', 'crmActivity', 'crmStage', 'crmDigest'],
	procurement: ['request', 'po', 'goodsIn', 'goodsOut'],
	inventory:   ['goodsIn', 'goodsOut', 'stockAction', 'stockAdjust', 'request', 'po'],
	catalogue:   ['catalogue'],
	accounting:  ['po', 'invoice', 'organisation'],
	auth:        ['user', 'organisation'],
	portal:      ['portalInvite', 'portalQuote', 'portalChangeOrder', 'portalInvoice', 'portalReset'],
};

export const ENTITY_INCLUDES: { [entity: string]: string[] } = {
	task: ['job'],
	crmActivity: ['crmRecord'],
	crmStage:    ['crmRecord'],
};

export const TRIGGER_EXCLUDED_TAGS: { [triggerId: string]: string[] } = {
	'project.invoice.sent.accounts': ['{Invoice Type}', '{Payment Status}', '{Payment Amount}', '{Amount Paid}', '{Amount Remaining}', '{Due Date}'],
	'project.invoice.paid':          ['{Due Date}'],
	'project.invoice.overdue':       ['{Payment Status}', '{Payment Amount}'],
	'project.invoice.payment.pending': ['{Due Date}'],
	'project.invoice.payment.failed':  ['{Due Date}'],
	// {Assigned To} is set only by the two triggers that say somebody was given something.
	'crm.activity.due':       ['{Assigned To}'],
	'crm.activity.overdue':   ['{Assigned To}'],
	'crm.mention':            ['{Assigned To}'],
	'crm.deal.stage_changed': ['{Assigned To}'],
};

export const TRIGGER_ENTITY: { [triggerId: string]: string } = {
	// WorkHub
	'workhub.job.assigned':           'job',
	'workhub.booking.changed':        'bookingChange',
	'workhub.booking.note.added':     'bookingNote',
	'workhub.booking.note.edited':    'bookingNote',
	'workhub.task.note.added':        'task',
	'workhub.task.completed':         'task',
	'workhub.stock.variation':        'stockVariation',
	'workhub.report.created':         'report',
	'workhub.follow_up.requested':    'followUp',
	'workhub.follow_up.resolved':     'followUp',
	'workhub.snagg.session.overtime': 'job',
	'workhub.snagg.break.overtime':   'job',
	'workhub.job.completed':          'job',
	'workhub.task.billable.complete': 'task',
	'workhub.timesheet.approval':     'timesheet',

	// CRM
	'crm.assigned':            'crmRecord',
	'crm.activity.assigned':   'crmActivity',
	'crm.activity.due':        'crmActivity',
	'crm.activity.overdue':    'crmActivity',
	'crm.mention':             'crmActivity',
	'crm.deal.stage_changed':  'crmStage',
	'crm.digest.daily':        'crmDigest',

	// Quoting
	'quote.digest.uninvoiced':              'uninvoiced',
	'quote.submit.review':            'quote',
	'quote.review.result':            'quote',
	'quote.passed.manually':          'quote',
	'quote.proposal.viewed':          'quote',
	'quote.proposal.accepted':        'quote',
	'quote.proposal.comment':         'quote',
	'quote.changeorder.viewed':       'changeOrder',
	'quote.changeorder.accepted':     'changeOrder',
	'quote.salesorder.completed':     'salesOrder',
	'quote.quota.exceeded':           'organisation',
	'quote.expired':                  'quote',

	// Projects
	'project.invoice.sent.accounts':  'invoice',
	'project.invoice.paid':           'invoice',
	'project.invoice.overdue':        'invoice',
	'project.invoice.payment.pending': 'invoice',
	'project.invoice.payment.failed': 'invoice',

	// Snagg
	'snagg.user.connected':           'snaggUser',
	'snagg.created':                  'snagg',

	// Procurement
	'proc.stock.request.submitted':   'request',
	'proc.po.sent.supplier':          'po',
	'proc.po.sent.internal':          'po',
	'proc.po.confirmed':              'po',
	'proc.po.not.received':           'po',
	'proc.po.sent.integration':       'po',
	'proc.request.finalised':         'request',
	'proc.request.stock.items':       'request',
	'proc.request.overdue':           'request',

	// Inventory
	'inv.goods.in.complete':          'goodsIn',
	'inv.goods.out.complete':         'goodsOut',
	'inv.goods.in.not.received':      'goodsIn',
	'inv.goods.out.picking.ready':    'goodsOut',
	'inv.goods.out.dispatch.ready':   'goodsOut',
	'inv.stock.free':                 'stockAction',
	'inv.stock.reassigned':           'stockAction',
	'inv.stock.adjusted':             'stockAdjust',

	// Catalogue
	'cat.access.request':             'catalogue',
	'cat.access.approved':            'catalogue',
	'cat.access.cancelled':           'catalogue',
	'cat.subscription.pending.digest':'catalogueDigest',

	// Portal
	'portal.user.invite':             'portalInvite',
	'portal.quote.added':             'portalQuote',
	'portal.change_order.added':      'portalChangeOrder',
	'portal.invoice.added':           'portalInvoice',
	'portal.password.reset.request':  'portalReset',
};



export const NOTIFICATION_DEFAULT_BLOCKS = (tpl: NotificationTemplate): NotificationBlock[] => [
	{ id: 'b1', type: 'header', brand: 'WeQuote', accent: '#FF4655' },
	{ id: 'b2', type: 'eyebrow', text: 'NOTIFICATION' },
	{ id: 'b3', type: 'heading', text: tpl.subject || 'Subject line goes here' },
	{ id: 'b4', type: 'text', text: tpl.body || 'Hi {Recipient Name},\n\nThis is the master body. Edit blocks on the right.' },
	{ id: 'b5', type: 'meta', columns: 1, rows: [
		{ k: 'Project', v: '{Project Name}' },
		{ k: 'Due', v: '{Due Date}' },
		{ k: 'Assigned by', v: '{Sender Name}' },
	] },
	{ id: 'b6', type: 'button', label: 'Open in WeQuote', url: '{Action URL}', color: '#FF4655' },
	{ id: 'b7', type: 'divider' },
	{ id: 'b8', type: 'footer',
		copyright: '© 2025 Ouitech Ltd. All rights reserved.',
		companyInfo: 'Company number 12576882 · VAT number 374037596',
		address: 'Alderley Park, Glass House, Alderley Edge, SK10 4TF',
		contactEmail: 'info@wetech.cloud',
		socials: [
			{ network: 'linkedin', url: 'https://www.linkedin.com/company/wequote/' },
			{ network: 'facebook', url: 'https://www.facebook.com/wequote.cloud' },
			{ network: 'instagram', url: 'https://www.instagram.com/wequote.cloud/' },
		],
	},
];

export type SesStatus = 'queued' | 'sending' | 'sent' | 'delivered' | 'opened' | 'clicked'
	| 'bounced' | 'failed' | 'rejected' | 'suppressed';

export interface NotificationLogRow {
	id: string;
	ts: number;
	orgId: string;
	orgName: string;
	orgInitials: string;
	orgColor: string;
	to: string;
	toName: string;
	from: string;
	fromName: string;
	subject: string;
	triggerId: string;
	triggerLabel: string;
	template: string;
	templateName?: string;
	status: SesStatus;
	attempts: number;
	bytes: number;
	deliveryMs?: number;
	sesId: string;
	bounce?: { type: 'Permanent' | 'Transient'; sub: string; text: string };
	events: { ts: number; type: string; desc: string }[];
}

export interface OrgUsageRow {
	orgId: string;
	name: string;
	initials: string;
	color: string;
	plan: 'Pro' | 'Standard' | 'Trial';
	thisMonth: number;
	trend: number;
	bounced: number;
	bounceRate: number;
	quota: number;
	cost: number;
	monthly: { month: string; sent: number }[];
}

export interface LogFilters {
	range?: '24h' | '7d' | '30d' | '90d';
	statuses?: string[];
	triggers?: string[];
	orgIds?: string[];
}

export const SES_STATUSES: { key: SesStatus; label: string; color: string }[] = [
	{ key: 'queued',     label: 'Queued',     color: 'secondary' },
	{ key: 'sending',    label: 'Sending',    color: 'info'      },
	{ key: 'sent',       label: 'Sent',       color: 'info'      },
	{ key: 'delivered',  label: 'Delivered',  color: 'secondary' },
	{ key: 'opened',     label: 'Opened',     color: 'success'   },
	{ key: 'clicked',    label: 'Clicked',    color: 'primary'   },
	{ key: 'bounced',    label: 'Bounced',    color: 'danger'    },
	{ key: 'failed',     label: 'Failed',     color: 'danger'    },
	{ key: 'rejected',   label: 'Rejected',   color: 'danger'    },
	{ key: 'suppressed', label: 'Suppressed', color: 'warning'   },
];

export const SES_RESEND_STATUSES: SesStatus[] = ['failed', 'bounced', 'suppressed'];

export const LOG_DATE_RANGES = [
	{ key: '24h', label: 'Last 24 hours', ms: 24 * 3600 * 1000 },
	{ key: '7d',  label: 'Last 7 days',   ms: 7 * 24 * 3600 * 1000 },
	{ key: '30d', label: 'Last 30 days',  ms: 30 * 24 * 3600 * 1000 },
	{ key: '90d', label: 'Last 90 days',  ms: 90 * 24 * 3600 * 1000 },
];


@Injectable({ providedIn: 'root' })
export class SettingsNotificationFixtureService {

	tagGroupsForTrigger(trigger?: { id?: string; module?: string } | null): NotificationTagGroup[] {
		if (!trigger) return [COMMON_TAGS];
		const primary = this.resolvePrimaryEntity(trigger.id);
		let groups: NotificationTagGroup[];
		if (primary) groups = this.tagGroupsForEntities([primary]);
		else if (trigger.module) groups = this.tagGroupsForModule(trigger.module);
		else return [COMMON_TAGS];
		return this.excludeTagsForTrigger(groups, trigger.id);
	}

	private excludeTagsForTrigger(groups: NotificationTagGroup[], triggerId?: string): NotificationTagGroup[] {
		const excluded = triggerId ? TRIGGER_EXCLUDED_TAGS[triggerId] : null;
		if (!excluded || !excluded.length) return groups;
		const remove = new Set(excluded);
		return groups.map(g => ({ ...g, tags: g.tags.filter(t => !remove.has(t)) }));
	}

	private resolvePrimaryEntity(triggerId?: string): string | null {
		if (!triggerId) return null;
		if (TRIGGER_ENTITY[triggerId]) return TRIGGER_ENTITY[triggerId];

		const parts = triggerId.split('.');
		if (parts.length >= 2 && ENTITY_TAG_GROUPS[parts[1]]) return parts[1];

		return null;
	}

	tagGroupsForModule(moduleKey: string): NotificationTagGroup[] {
		return this.tagGroupsForEntities(MODULE_ENTITIES[moduleKey] || []);
	}

	private tagGroupsForEntities(entities: string[]): NotificationTagGroup[] {
		const seen = new Set<string>();
		const groups: NotificationTagGroup[] = [COMMON_TAGS];
		const expand = (e: string) => {
			if (seen.has(e)) return;
			seen.add(e);
			(ENTITY_INCLUDES[e] || []).forEach(expand);
			if (ENTITY_TAG_GROUPS[e]) groups.push(ENTITY_TAG_GROUPS[e]);
		};
		entities.forEach(expand);
		return groups;
	}

	tagGroupsAll(): NotificationTagGroup[] {
		return [COMMON_TAGS, ...Object.keys(ENTITY_TAG_GROUPS).map(k => ENTITY_TAG_GROUPS[k])];
	}
}
